import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSocketConnectionId } from '@/contexts/SocketContext';
import CorgeeStorageHelper from '../Globals/CorgeeStorageHelper';
import ConversationLayers from '../Globals/CardConversationLayers';
import type { CardConversationLayersRef } from '../Globals/CardConversationLayers';
import ConversationProjects from '../Globals/ConversationProjects';
import ConversationSecurityGroups from '../Globals/ConversationSecurityGroups';
import ConversationGroupLayers from '../Globals/ConversationGroupLayers';
import * as apiHelper from '../Globals/Utils';
import ConversationOwner from '../Globals/ConversationOwner';
import EditCard from '../EditCard/EditCard';
import type { EditCardRef, EditCardConfig } from '../EditCard/EditCard';
import { conversationBus } from '@/lib/conversationBus';
import './HeaderDetails.css';

// Types
interface ApiConfig {
  url: string;
  token: string;
}

interface Layer {
  LayerId: number;
  isForAdd?: boolean;
  [key: string]: any;
}

interface ConversationGroup {
  GroupId: number;
  IsDefault?: boolean;
  [key: string]: any;
}

interface Conversation {
  ConfigValueId?: number;
  ConfigValueID?: number;
  selectedConfigValueId?: number;
  ProjectId?: number;
  ProjectID?: number;
  ConversationLayers?: Layer[];
  ConversationGroups?: ConversationGroup[];
  layers?: Layer[];
  MilestoneId?: number;
  MiniatureDefaultImageName?: string;
  ProjectBody?: string;
  ProjectConversationId?: number;
  ProjectName?: string;
  SideMenuText?: string;
  Title?: string;
  UpdatedAt?: string;
  UpdatedBy?: string;
  isUserFollowingConversation?: boolean;
  OwnedById?: number;
  OwnedBy?: string;
  dashboardGroupId?: number;
  isAddLayerToCard?: boolean;
  layerCustomFields?: any;
  [key: string]: any;
}

interface Config {
  apiUrl: string;
  authToken: string;
  addLayerToCard?: (
    e: React.MouseEvent | null,
    conversation: Conversation | Layer,
    layer: Layer | null,
    callback?: (layerData: Layer) => void
  ) => void;
  addLayerToProject?: (
    e: React.MouseEvent,
    layer: Layer,
    functions?: { onUpdateLayerToCard?: (layerId: number) => void }
  ) => void;
  onChangeLayers?: (conversation: Conversation, isFromInfoWindow?: boolean) => void;
  onChangedProject?: (e: React.MouseEvent | undefined, conversation: Conversation) => void;
  onChangedSecurityGroups?: (e: React.MouseEvent, params: any) => void;
  onChangedGroupLayers?: (e: React.MouseEvent, params: any) => void;
  onChangeOwners?: (conversation: Conversation, isFromInfoWindow?: boolean) => void;
  section?: string;
  [key: string]: any;
}

interface HeaderDetailsProps {
  config: Config;
  item: Conversation;
  disableAutoSave?: boolean;
  isNew?: boolean;
  configData?: any;
  onUpdateConversationGroups?: (conversation: Conversation) => void;
  sendDataToCallback?: (data: any) => void;
  onReloadLayers?: (layers: Layer[]) => void;
  isConversationDetailsView?: boolean;
  /* When true, return only the <li> fields (no wrapping div/ul, no EditCard) —
     lets a parent merge these fields into a shared <ul> with another list. */
  fieldsOnly?: boolean;
  /* When true, the project is fixed by the surrounding scope (e.g. a project
     dashboard) and the Project selector is hidden. isProjectView below only
     covers the legacy /ProjectDetails URL, and its `isNew ||` branch shows the
     field for new cards regardless, which let inline tasks be created against
     another project. */
  lockProject?: boolean;
  /* Forces the Layers picker to keep at least one layer even on new cards (inline creation) */
  keepOneLayer?: boolean;
}

// Using CardConversationLayersRef from CardConversationLayers
type ConversationLayersRef = CardConversationLayersRef;

interface ConversationGroupLayersRef {
  onChangeParentLayers: (layers: Layer[]) => void;
  onChangeGroupsLayers: (groups: ConversationGroup[]) => void;
}

interface ConversationSecurityGroupsRef {
  setDefaultGroup: (group: ConversationGroup) => void;
}

export interface HeaderDetailsRef {
  updateConversation: (newConversation: Conversation) => void;
  onUpdateLayersConversation: (layers: Layer[]) => void;
}

const HeaderDetails = React.forwardRef<HeaderDetailsRef, HeaderDetailsProps>(
  (
    {
      config,
      item,
      disableAutoSave = false,
      isNew = false,
      keepOneLayer = false,
      configData,
      onUpdateConversationGroups,
      sendDataToCallback,
      onReloadLayers,
      isConversationDetailsView,
      fieldsOnly = false,
      lockProject = false,
    },
    ref,
  ) =>
  {
    const socketConnectionId = useSocketConnectionId();
    const [apiConfig] = useState<ApiConfig>({
      url: config.apiUrl,
      token: config.authToken,
    });

    const [conversation, setConversation] = useState<Conversation>(item);
    const [projectNameConfig] = useState<string>(CorgeeStorageHelper.getProjectNameConfig());
    const [personId] = useState<number>(CorgeeStorageHelper.personId());

    const isProject = useMemo(() =>
    {
      const configValueId = item?.ConfigValueId || item?.ConfigValueID;
      return configValueId === 9;
    }, [item]);

    const isPeople = useMemo(() => item?.ConfigValueId === 15, [item]);

    const isDashboardView = useMemo(() => window.location.href.indexOf('Dashboards') > -1, []);

    const isProjectView = useMemo(() => window.location.href.indexOf('ProjectDetails') > -1, []);

    const isConversationDetails = useMemo(
      () => window.location.href.indexOf('ConversationDetails') > -1,
      [],
    );

    // setState updaters must stay pure: they queue their side effects here and the
    // effect below flushes them after commit. Keyed by name to dedupe StrictMode reruns.
    const afterCommitRef = useRef<Map<string, () => void>>(new Map());

    const queueAfterCommit = useCallback((key: string, fn: () => void) =>
    {
      afterCommitRef.current.set(key, fn);
    }, []);

    useEffect(() =>
    {
      if (afterCommitRef.current.size === 0) return;

      const pending = [...afterCommitRef.current.values()];
      afterCommitRef.current.clear();
      pending.forEach(fn => fn());
    });

    // Refs
    const conversationLayersRef = useRef<ConversationLayersRef>(null);
    const conversationGroupLayersRef = useRef<ConversationGroupLayersRef>(null);
    const conversationSecurityGroupsRef = useRef<ConversationSecurityGroupsRef>(null);
    const editCardRef = useRef<EditCardRef>(null);

    React.useImperativeHandle(ref, () => ({
      updateConversation: (newConversation: Conversation) =>
      {
        setConversation(newConversation);
      },
      onUpdateLayersConversation: (layers: Layer[]) =>
      {
        onUpdateLayersConversation(layers);
      },
    }));

    const validationLayers = useCallback(
      (actualLayers: Layer[], updateLayers: Layer[]): boolean =>
      {
        if (actualLayers.length !== updateLayers.length) return true;
        if (JSON.stringify(actualLayers) !== JSON.stringify(updateLayers)) return true;
        return false;
      },
      [],
    );

    const onUpdateLayersConversation = useCallback((conversationLayers: Layer[]) =>
    {
      const filteredLayers =
        conversationLayers && conversationLayers.length > 0
          ? conversationLayers.filter(x => x.LayerId !== 0)
          : [];

      setConversation(prev => ({
        ...prev,
        ConversationLayers: filteredLayers,
      }));

      if (conversationLayersRef.current)
      {
        conversationLayersRef.current.onSelectLayer(filteredLayers);
        conversationLayersRef.current.onUpdateOriginalSelectedIds(filteredLayers);
      }

      if (conversationGroupLayersRef.current)
      {
        conversationGroupLayersRef.current.onChangeParentLayers(filteredLayers);
      }
    }, []);

    useEffect(() =>
    {
      if (!item?.ConversationLayers) return;
      const filteredLayers = item.ConversationLayers.filter(x => x.LayerId !== 0);
      setConversation(prev =>
      {
        const prevIds = (prev.ConversationLayers || [])
          .map(l => l.LayerId)
          .sort()
          .join(',');
        const newIds = filteredLayers.map(l => l.LayerId).sort().join(',');
        if (prevIds === newIds) return prev;
        return { ...prev, ConversationLayers: filteredLayers };
      });
      if (conversationLayersRef.current)
      {
        conversationLayersRef.current.onSelectLayer(filteredLayers);
        conversationLayersRef.current.onUpdateOriginalSelectedIds(filteredLayers);
      }
      if (conversationGroupLayersRef.current)
      {
        conversationGroupLayersRef.current.onChangeParentLayers(filteredLayers);
      }
    }, [item?.ConversationLayers]);

    const addLayerToCard = useCallback(
      (e: React.MouseEvent, layer: Layer) =>
      {
        if (config.addLayerToCard && !isConversationDetailsView)
        {
          const conversationCopy: Conversation = {
            ...conversation,
            ConfigValueId: conversation.ConfigValueId ?? conversation.selectedConfigValueId,
          };

          config.addLayerToCard(e, conversationCopy, layer, (layerData: Layer) =>
          {
            if (isNew)
            {
              setConversation(prev =>
              {
                const newLayersSelected = (prev.ConversationLayers || []).filter(
                  item => !item.isForAdd,
                );
                newLayersSelected.push(layerData);

                const updated: Conversation = {
                  ...prev,
                  ConversationLayers: newLayersSelected,
                  isAddLayerToCard: true,
                };

                queueAfterCommit('onChangeLayers', () => config.onChangeLayers?.(updated));

                return updated;
              });
            }
          });
        }

        if (config.addLayerToCard && isConversationDetailsView)
        {
          config.addLayerToCard(e, layer, null);
        }
      },
      [config, conversation, isNew, isConversationDetailsView, queueAfterCommit],
    );

    const addLayerToProject = useCallback(
      (e: React.MouseEvent, layer: Layer, functions?: { onUpdateLayerToCard?: (layerId: number) => void }) =>
      {
        config.addLayerToProject?.(e, layer, functions);
      },
      [config],
    );

    const reloadLayers = useCallback(
      (conversationOverride?: Conversation) =>
      {
        const currentConversation = conversationOverride || conversation;
        const queryParts: string[] = [];
        if (currentConversation.selectedConfigValueId)
        {
          queryParts.push(`configValueId=${currentConversation.selectedConfigValueId}`);
        }
        if (currentConversation.ProjectID || currentConversation.ProjectId)
        {
          const projectId = currentConversation.ProjectID || currentConversation.ProjectId;
          queryParts.push(`projectId=${projectId}`);
        }
        const params = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';

        const userDefaultLayerId = CorgeeStorageHelper.layerId();

        apiHelper
          .get<Layer[]>(apiConfig, `api/layer/GetLayersByConfigValueId${params}`)
          .then(layersResponse =>
          {
            if (!Array.isArray(layersResponse)) return;
            setConversation(prev =>
            {
              // 'prev' already carries the new project info; only the layers are new here
              const conversationCopy: Conversation = {
                ...prev,
                layers: layersResponse,
              };

              let selectedLayer = layersResponse.find(
                item => item.LayerId === parseInt(userDefaultLayerId || '0'),
              );

              if (selectedLayer && CorgeeStorageHelper.isLayerHidden(selectedLayer.LayerId))
              {
                selectedLayer = undefined;
              }

              if (!selectedLayer)
              {
                const userLayers = CorgeeStorageHelper.getAllLayers();
                outerLoop: for (let i = 0; i < userLayers.length; i++)
                {
                  for (let j = 0; j < layersResponse.length; j++)
                  {
                    if (
                      userLayers[i]?.LayerId === layersResponse[j]?.LayerId &&
                      !CorgeeStorageHelper.isLayerHidden(layersResponse[j]?.LayerId || 0)
                    )
                    {
                      selectedLayer = layersResponse[j];
                      break outerLoop;
                    }
                  }
                }
              }

              conversationCopy.ConversationLayers = selectedLayer ? [selectedLayer] : [];

              queueAfterCommit('onChangedProject', () =>
                config.onChangedProject?.(undefined, conversationCopy),
              );

              return conversationCopy;
            });
          })
          .catch(error =>
          {
            console.warn('Api Error', error);
          });
      },
      [apiConfig, conversation, config, queueAfterCommit],
    );

    const onChangedItemType = useCallback(
      (newItemType: { ConfigValueId: number; DashboardGroupId?: number }) =>
      {
        if (newItemType.ConfigValueId === 9)
        {
          apiHelper
            .get<ConversationGroup[]>(apiConfig, 'api/group/GetGroups')
            .then(groupsResponse =>
            {
              apiHelper
                .get<number>(apiConfig, `api/Person/PersonGroupProject?PersonId=${personId}`)
                .then(dataResponse =>
                {
                  if (groupsResponse.length > 0)
                  {
                    let groupDefault = groupsResponse.find(item => item.GroupId === dataResponse);
                    if (!groupDefault)
                    {
                      groupDefault = groupsResponse.find(item => item.IsDefault === true);
                    }

                    if (groupDefault)
                    {
                      const defaultGroup = groupDefault;
                      setConversation(prev =>
                      {
                        const updated: Conversation = {
                          ...prev,
                          ConversationGroups: [defaultGroup],
                        };

                        queueAfterCommit('onChangedItemType', () =>
                        {
                          onUpdateConversationGroups?.(updated);
                          conversationSecurityGroupsRef.current?.setDefaultGroup(defaultGroup);
                          sendDataToCallback?.(updated.layerCustomFields);
                        });

                        return updated;
                      });
                    }
                  }
                })
                .catch(error =>
                {
                  console.warn('Api Error', error);
                });
            })
            .catch(error =>
            {
              console.warn('Api Error', error);
            });
        }
      },
      [apiConfig, personId, onUpdateConversationGroups, sendDataToCallback, queueAfterCommit],
    );

    const onChangedSecurityGroups = useCallback(
      (e: React.MouseEvent, params: any) =>
      {
        if (isNew)
        {
          setConversation(prev =>
          {
            const updated: Conversation = {
              ...prev,
              ConversationGroups: params.ConversationGroups,
              ConversationLayers: params.ConversationLayers,
            };

            queueAfterCommit('onChangedSecurityGroups', () =>
            {
              onUpdateConversationGroups?.(updated);
              config.onChangedSecurityGroups?.(e, updated);
              conversationGroupLayersRef.current?.onChangeGroupsLayers(params.ConversationGroups);
            });

            return updated;
          });
        }
        else
        {
          if (!isNew && isProject && editCardRef.current)
          {
            editCardRef.current.openModal(conversation);
            return;
          }

          if (config.onChangedSecurityGroups)
          {
            config.onChangedSecurityGroups(e, params);
          }
        }
      },
      [isNew, config, onUpdateConversationGroups, isProject, queueAfterCommit],
    );

    const onChangedGroupLayers = useCallback(
      (e: React.MouseEvent, params: any) =>
      {
        if (!isNew && isProject && editCardRef.current)
        {
          editCardRef.current.openModal(conversation);
          return;
        }

        if (config.onChangedGroupLayers)
        {
          config.onChangedGroupLayers(e, params);
        }
      },
      [isNew, config, isProject],
    );

    const onChangedProject = useCallback(
      (_e: React.MouseEvent | undefined, params: Conversation) =>
      {
        const updatedParams = { ...params };
        // If project changed, clear dependent fields
        if (updatedParams.ProjectId !== conversation.ProjectId)
        {
          updatedParams.OwnedById = undefined;
          updatedParams.OwnedBy = '';
          updatedParams.ConversationGroups = [];
          // Note: Layers are cleared/reloaded by reloadLayers
        }

        setConversation(updatedParams);

        if (isNew)
        {
          setTimeout(() =>
          {
            reloadLayers(updatedParams);
          }, 0);
        }
        else
        {
          if (editCardRef.current)
          {
            editCardRef.current.openModal(conversation);
          }
        }
      },
      [isNew, reloadLayers, conversation],
    );

    const onChangedLayers = useCallback(
      (params: Conversation, isFromInfoWindow?: boolean) =>
      {
        setConversation(params);

        if (config.onChangeLayers)
        {
          config.onChangeLayers(params, isFromInfoWindow);
        }

        if (onReloadLayers && params.ConversationLayers)
        {
          onReloadLayers(params.ConversationLayers);
        }
      },
      [config, onReloadLayers],
    );

    const onChangedOwner = useCallback(
      (params: Conversation, isFromInfoWindow?: boolean) =>
      {
        setConversation(params);

        if (config.onChangeOwners)
        {
          config.onChangeOwners(params, isFromInfoWindow);
        }
      },
      [config],
    );

    // Sync with props.item; a ref tracks the previous item to avoid infinite loops
    const prevItemRef = useRef<Conversation | null>(null);

    useEffect(() =>
    {
      // Skip if item has not changed
      if (prevItemRef.current === item)
      {
        return;
      }
      prevItemRef.current = item;

      const itemLayers =
        (item?.ConversationLayers?.length ?? 0 > 0)
          ? item.ConversationLayers!.filter(x => x.LayerId !== 0)
          : [];

      setConversation(prev =>
      {
        const stateLayers =
          (prev?.ConversationLayers?.length ?? 0 > 0)
            ? prev.ConversationLayers!.filter(x => x.LayerId !== 0)
            : [];

        let needsUpdate = false;
        const updates: Partial<Conversation> = {};

        // Sync groups and layers for projects
        if (
          prev &&
          item &&
          prev.ConfigValueId === 9 &&
          prev.ConversationGroups !== undefined &&
          prev.ConversationGroups !== null &&
          item.ConversationGroups !== undefined &&
          item.ConversationGroups !== null &&
          item.ConversationGroups.length > 0 &&
          (prev.ConversationGroups.length !== item.ConversationGroups.length ||
            stateLayers.length !== itemLayers.length) &&
          (isProjectView || isConversationDetails || isDashboardView)
        )
        {
          updates.ConversationGroups = item.ConversationGroups;
          updates.ConversationLayers = itemLayers;
          needsUpdate = true;

          if (itemLayers)
          {
            queueAfterCommit('onChangeParentLayers', () =>
              conversationGroupLayersRef.current?.onChangeParentLayers(itemLayers),
            );
          }
        }

        // Sync ConfigValueId
        if (
          prev &&
          item &&
          (prev.ConfigValueId !== item.ConfigValueId ||
            prev.selectedConfigValueId !== item.selectedConfigValueId)
        )
        {
          updates.ConfigValueId = item.selectedConfigValueId || item.ConfigValueId;
          updates.selectedConfigValueId = item.selectedConfigValueId || item.ConfigValueId;
          needsUpdate = true;
        }

        // Sync project properties
        if (
          prev &&
          item &&
          (prev.MilestoneId !== item.MilestoneId || prev.ProjectId !== item.ProjectId)
        )
        {
          updates.ConversationLayers = itemLayers;
          updates.MilestoneId = item.MilestoneId;
          updates.MiniatureDefaultImageName = item.MiniatureDefaultImageName;
          updates.ProjectBody = item.ProjectBody;
          updates.ProjectConversationId = item.ProjectConversationId;
          updates.ProjectId = item.ProjectId;
          updates.ProjectName = item.ProjectName;
          updates.SideMenuText = item.SideMenuText;
          updates.Title = item.Title;
          updates.UpdatedAt = item.UpdatedAt;
          updates.UpdatedBy = item.UpdatedBy;
          updates.isUserFollowingConversation = item.isUserFollowingConversation;
          needsUpdate = true;
        }

        // Validar y sincronizar layers
        if (prev && item && validationLayers(stateLayers, itemLayers))
        {
          updates.ConversationLayers = itemLayers;
          needsUpdate = true;
        }

        // Sync owner
        if (prev?.OwnedById !== item?.OwnedById)
        {
          updates.OwnedById = item.OwnedById;
          updates.OwnedBy = item.OwnedBy;
          needsUpdate = true;
        }

        if (needsUpdate)
        {
          return { ...prev, ...updates };
        }
        return prev;
      });
    }, [
      item,
      validationLayers,
      isProjectView,
      isConversationDetails,
      isDashboardView,
      queueAfterCommit,
    ]);

    // Mount
    useEffect(() =>
    {
      if (
        isProject &&
        conversation.ConfigValueId === 9 &&
        (!conversation.ConversationGroups || conversation.ConversationGroups.length === 0)
      )
      {
        apiHelper
          .get<ConversationGroup[]>(
            apiConfig,
            `api/group/GetGroupsByProject?projectId=${conversation.ProjectId}`,
          )
          .then(dataResponse =>
          {
            if (dataResponse.length > 0)
            {
              setConversation(prev =>
              {
                const updated: Conversation = {
                  ...prev,
                  ConversationGroups: dataResponse,
                };

                queueAfterCommit('onUpdateConversationGroups', () =>
                  onUpdateConversationGroups?.(updated),
                );

                return updated;
              });
            }
          })
          .catch(error =>
          {
            console.warn('Api Error', error);
          });
      }

      if (isProject && isNew)
      {
        const newItemType = {
          ConfigValueId: 9,
          DashboardGroupId: conversation.dashboardGroupId,
        };
        onChangedItemType(newItemType);
      }

      if (conversation && conversation.ConfigValueId !== 9 && isNew)
      {
        reloadLayers();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Solo en mount

    const editCardConfig: EditCardConfig = useMemo(
      () => ({
        apiUrl: apiConfig.url,
        authToken: apiConfig.token,
        SocketConnectionId: socketConnectionId,
        functions: {
          loadingBarStart: () => {},
          loadingBarComplete: () => {},
          conversationResponse: (_requestConfig: any, response: any) =>
          {
            ref && typeof ref !== 'function' && ref.current?.updateConversation(response);
            if (response?.ConversationId)
            {
              conversationBus.emit('conversation-updated', {
                conversationId: response.ConversationId,
                updates: response,
              });
            }
          },
        },
        params: { Conversation: conversation },
      }),
      [apiConfig, conversation, sendDataToCallback, ref, socketConnectionId],
    );

    const fieldsList = (
      <>
        {isProject && !isPeople && (
          <li className="corgee_com_custom-fields-field-A _DEL_field-A group-editable-field-item">
            <span className="corgee_com_custom-fields-title _DEL_title">Security Groups</span>
            <ConversationSecurityGroups
              apiConfig={apiConfig}
              conversation={conversation as any}
              ref={conversationSecurityGroupsRef}
              isNew={isNew}
              disableAutoSave={disableAutoSave}
              onChangedSecurityGroups={onChangedSecurityGroups}
            />
          </li>
        )}

        {isProject && !isPeople && (
          <li className="corgee_com_custom-fields-field-A _DEL_field-A group-layer-editable-field-item">
            <span className="corgee_com_custom-fields-title _DEL_title">Layers</span>
            <ConversationGroupLayers
              apiConfig={apiConfig}
              conversation={conversation as any}
              ref={conversationGroupLayersRef}
              isNew={isNew}
              disableAutoSave={disableAutoSave}
              onChangedGroupLayers={onChangedGroupLayers}
            />
          </li>
        )}

        {!isProject && !lockProject && (isNew || (!isPeople && !isProjectView)) && (
          <li className="corgee_com_custom-fields-field-A _DEL_field-A project-editable-field-item">
            <span className="corgee_com_custom-fields-title _DEL_title">{projectNameConfig}</span>
            <ConversationProjects
              apiConfig={apiConfig}
              conversation={conversation as any}
              isNew={isNew}
              disableAutoSave={disableAutoSave}
              onChangedProject={onChangedProject as any}
            />
          </li>
        )}

        {!isProject && (isNew || !isPeople) && (
          <li className="corgee_com_custom-fields-field-A _DEL_field-A layer-editable-field-item">
            <span className="corgee_com_custom-fields-title _DEL_title">Layers</span>
            <ConversationLayers
              isNew={isNew}
              apiConfig={apiConfig}
              conversation={conversation as any}
              ref={conversationLayersRef as any}
              disableAutoSave={disableAutoSave}
              keepOneLayer={keepOneLayer}
              onChangedLayers={onChangedLayers as any}
              addLayerToCard={addLayerToCard as any}
              addLayerToProject={addLayerToProject as any}
              section={config.section}
              configData={configData}
              hideAddLayerPrompt={true}
            />
          </li>
        )}

        {!isProject && (isNew || !isPeople) && (
          <li className="corgee_com_custom-fields-field-A _DEL_field-A project-editable-field-item">
            <span className="corgee_com_custom-fields-title _DEL_title">Card Owner</span>
            <ConversationOwner
              isNew={isNew}
              apiConfig={apiConfig}
              conversation={conversation as any}
              disableAutoSave={disableAutoSave}
              onChangedOwner={onChangedOwner as any}
              configData={configData}
              config={config}
            />
          </li>
        )}
      </>
    );

    if (fieldsOnly)
    {
      return fieldsList;
    }

    return (
      <div className="corgee_com_custom-fields-color-wrapper _DEL_color-wrapper no-layer">
        <EditCard ref={editCardRef} config={editCardConfig} UpdateCustomFields={() => {}} />
        <ul className="_DEL_inner">
          {fieldsList}
        </ul>
      </div>
    );
  },
);

export default HeaderDetails;
